/** Creator OG helper interpretation, per DIY spec v1.1. No URLs or OG payloads. */
export type OgCompletion =
  | { outcome: "success" }
  | { outcome: "error"; errorCode: "no_data" }
  | { outcome: "timeout" }
  | { outcome: "cancelled" };

const validTime = (time: number) => Number.isFinite(time) && time >= 0;

/** Classify before rounding: a null at 3499.9ms is still no_data. */
export function classifyOgCompletion(
  hasData: boolean,
  issuedAt: number,
  settledAt: number
): OgCompletion | undefined {
  if (!validTime(issuedAt) || !validTime(settledAt) || settledAt < issuedAt)
    return undefined;
  if (hasData) return { outcome: "success" };
  return settledAt - issuedAt < 3500
    ? { outcome: "error", errorCode: "no_data" }
    : { outcome: "timeout" };
}

/** Same monotonic clock; resolved before tap means zero, not total prefetch time. */
export function submitOgWaitMs(
  tappedAt: number,
  settledAt: number | undefined
): number | undefined {
  // Pending/missing settlement is not proof of zero waiting.
  if (!validTime(tappedAt) || settledAt === undefined || !validTime(settledAt))
    return undefined;
  return Math.min(10000, Math.round(Math.max(0, settledAt - tappedAt)));
}

export interface OgPrefetchState {
  readonly sequence: number;
  readonly current?: {
    readonly id: number;
    readonly issuedAt: number;
  };
}

export type OgPrefetchEvent =
  | { type: "issued"; at: number }
  | { type: "settled"; id: number; at: number; hasData: boolean }
  | { type: "cancelled"; at: number };

export type OgPrefetchChange =
  | { type: "start"; id: number; at: number }
  | { type: "finish"; id: number; at: number; completion: OgCompletion };

/**
 * Bounded local observation bookkeeping, not a journey/span implementation.
 * Feed `issued` only when customFetchOgData actually runs, never on a cache hit.
 * The caller retains each returned id with its promise. No promise, timer, SDK,
 * business cancellation or payload lives here. Wiring waits for SDK beta.3.
 */
export function reduceOgPrefetch(
  state: OgPrefetchState,
  event: OgPrefetchEvent
): { state: OgPrefetchState; changes: OgPrefetchChange[] } {
  const { current } = state;
  const changes: OgPrefetchChange[] = [];
  if (!validTime(event.at) || (current && event.at < current.issuedAt))
    return { state, changes };

  if (event.type === "issued") {
    if (current)
      changes.push({
        type: "finish",
        id: current.id,
        at: event.at,
        completion: { outcome: "cancelled" },
      });
    const id = state.sequence + 1;
    changes.push({ type: "start", id, at: event.at });
    return {
      state: { sequence: id, current: { id, issuedAt: event.at } },
      changes,
    };
  }

  if (!current || (event.type === "settled" && event.id !== current.id))
    return { state, changes };
  const completion =
    event.type === "cancelled"
      ? ({ outcome: "cancelled" } as const)
      : classifyOgCompletion(event.hasData, current.issuedAt, event.at);
  if (!completion) return { state, changes };
  changes.push({ type: "finish", id: current.id, at: event.at, completion });
  // Preserve the sequence on cleanup so late settlements cannot hit a reused id.
  return { state: { sequence: state.sequence }, changes };
}
